import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spin, Select, message } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { INBOX_ENDPOINTS } from '../../constants/api';
import {
  MessageList,
  MessageBubble,
  MessageInputArea,
  MessageTextarea,
  SendButton,
  EmptyState,
  LoadingSpinner
} from '../../pages/Inbox/styled';
import { ConversationWrapper, Header, FromRow } from './styled';

/**
 * PatientConversation
 *
 * Embedded SMS thread for a single member/patient. Shows every SMS we've
 * exchanged with this person (including automated "ready for screening"
 * notifications) and lets staff send a message from the member's record.
 *
 * Props:
 *  - phoneNumber  (string) the member's phone number
 *  - contactName  (string) display name for the member
 */
const STATUS_LABELS = {
  queued: 'Queued',
  sending: 'Sending',
  sent: 'Sent',
  delivered: 'Delivered',
  failed: 'Failed',
  received: 'Received',
  read: 'Read'
};

const formatTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const PatientConversation = ({ phoneNumber, contactName }) => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [fromNumbers, setFromNumbers] = useState([]);
  const [selectedFrom, setSelectedFrom] = useState(null);

  const listRef = useRef(null);
  const conversationIdRef = useRef(null);

  const hasMessages = messages.length > 0;

  // Load the available BHA send-from numbers once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/inbox/from-numbers');
        if (cancelled) return;
        const nums = res?.data || [];
        setFromNumbers(nums);
        const def = nums.find((n) => n.isDefault) || nums[0];
        setSelectedFrom(def?.number || null);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the conversation for this phone number.
  const loadConversation = useCallback(async () => {
    if (!phoneNumber) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/inbox/conversations/by-phone', { phone: phoneNumber });
      const data = res?.data || {};
      const convId = data.conversation?.id || null;
      setConversationId(convId);
      conversationIdRef.current = convId;
      setMessages(data.messages || []);
    } catch {
      setConversationId(null);
      conversationIdRef.current = null;
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [phoneNumber]);

  useEffect(() => {
    loadConversation();
  }, [loadConversation]);

  // Scroll to newest message.
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  // Live updates for this conversation via the inbox WebSocket.
  useEffect(() => {
    let ws;
    let reconnectTimer;
    let closedByUs = false;

    const connect = () => {
      try {
        ws = new WebSocket(INBOX_ENDPOINTS.WEBSOCKET);
      } catch {
        return;
      }

      ws.onmessage = (event) => {
        let payload;
        try {
          payload = JSON.parse(event.data);
        } catch {
          return;
        }
        const { type, data } = payload;
        if (!type || !data) return;
        const myId = conversationIdRef.current;
        if (!myId || String(data.conversationId) !== String(myId)) return;

        if (type === 'inbox:newMessage') {
          setMessages((prev) =>
            prev.some((m) => String(m.id) === String(data.message.id))
              ? prev
              : [...prev, data.message]
          );
        } else if (type === 'inbox:messageStatusUpdated') {
          setMessages((prev) =>
            prev.map((m) =>
              String(m.id) === String(data.messageId) ? { ...m, status: data.status } : m
            )
          );
        }
      };

      ws.onclose = () => {
        if (!closedByUs) reconnectTimer = setTimeout(connect, 3000);
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          // ignore
        }
      };
    };

    connect();
    return () => {
      closedByUs = true;
      clearTimeout(reconnectTimer);
      try {
        ws?.close();
      } catch {
        // ignore
      }
    };
  }, []);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !phoneNumber) return;
    setSending(true);
    try {
      const isNew = !conversationId;
      const endpoint = isNew
        ? '/inbox/conversations/new/messages'
        : `/inbox/conversations/${conversationId}/messages`;
      // When the thread is empty, let staff pick which number to send from.
      // Otherwise the backend replies from the number the member last texted.
      const payload = isNew
        ? { text, phoneNumber, contactName, fromNumber: selectedFrom || undefined }
        : { text };

      const res = await api.post(endpoint, payload);
      if (!res?.success) {
        throw new Error(res?.message || res?.error || 'Send failed');
      }
      setDraft('');
      await loadConversation();
      if (res.data?.status === 'failed') {
        message.error('Message failed to send');
      } else {
        message.success('Message sent');
      }
    } catch (error) {
      message.error(error.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!phoneNumber) {
    return (
      <div style={{ padding: 24, color: '#999', textAlign: 'center' }}>
        No phone number on file for this member.
      </div>
    );
  }

  return (
    <ConversationWrapper>
      {/* Header */}
      <Header>
        <div className="name">{contactName || 'Member'}</div>
        <div className="to">To: <span>{phoneNumber}</span></div>
      </Header>

      {/* Messages */}
      <MessageList ref={listRef}>
        {loading ? (
          <LoadingSpinner>
            <Spin />
          </LoadingSpinner>
        ) : !hasMessages ? (
          <EmptyState>
            <div className="subtitle">No messages yet. Send one to start the conversation.</div>
          </EmptyState>
        ) : (
          messages.map((msg) => (
            <MessageBubble key={msg.id} outbound={msg.outbound}>
              {msg.text}
              <div className="message-time">
                {formatTime(msg.timestamp)}
                {msg.sentBy ? ` · ${msg.sentBy}` : ''}
                {msg.outbound && msg.status ? (
                  <span style={{ color: msg.status === 'failed' ? '#ffccc7' : 'inherit' }}>
                    {' '}· {STATUS_LABELS[msg.status] || msg.status}
                  </span>
                ) : null}
              </div>
            </MessageBubble>
          ))
        )}
      </MessageList>

      {/* Only let staff choose the sending number when the thread is empty. */}
      {!hasMessages && fromNumbers.length > 1 && (
        <FromRow>
          <span>Send from:</span>
          <Select
            size="small"
            value={selectedFrom}
            style={{ minWidth: 220 }}
            onChange={setSelectedFrom}
            options={fromNumbers.map((n) => ({
              value: n.number,
              label: `${n.number} (${n.label})`
            }))}
          />
        </FromRow>
      )}

      {/* Composer */}
      <MessageInputArea>
        <MessageTextarea
          placeholder="Type your message..."
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyPress={handleKeyPress}
          rows={1}
        />
        <SendButton onClick={handleSend} disabled={!draft.trim() || sending}>
          {sending ? <Spin size="small" /> : <SendOutlined />}
        </SendButton>
      </MessageInputArea>
    </ConversationWrapper>
  );
};

export default PatientConversation;
